// Full-screen POS page at /pos.
// Layout: toolbar (tabs+barcode+chips) | left=cart | right=payment panel (lg+)
//
// Serial-first flow:
//   BarcodeInput is given a serialResolver (useScanResolve sell-context).
//   kind=serial&ok → ensureDraft → add/find line → attach serial via updateItem.
//   kind=serial&!ok → toast reason.
//   kind=variant → existing onAdd/onMultiple flow unchanged.
//
// Per-line serial scan: CartItemList threads serialResolver + onAttach/onDetach
//   down to CartItemCard. Each item's SerialArea resolves independently.
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { WarehouseIcon } from "lucide-react";
import { usePosCart, WorkstationNotConfiguredError } from "./use-pos-cart";
import { CartItemList } from "./cart-item-list";
import { ProductSearchModal } from "./product-search-modal";
import { PaymentPanel, type PaymentView } from "./payment-panel";
import { MobilePaymentBar } from "./mobile-payment-bar";
import { PosToolbar } from "./pos-toolbar";
import { WorkstationSelector } from "./workstation-selector";
import { apiPost, apiGet } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import { useScanResolve, type ScanContext, type ScanResult } from "@/features/inventory/use-scan-resolve";
import { useAuthStore } from "@/lib/auth/store";

interface CouponResult { code: string; amountOff: number; }

export function PosScreenPage() {
  const {
    order,
    drafts,
    addItem,
    updateItem,
    removeItem,
    patchHeader,
    commit,
    createDraft,
    ensureDraft,
    closeDraft,
    deleteDraft,
    resumeDraft,
    clearCart,
    orderType,
    setOrderType,
    imageCache,
    cacheVariantImage,
    retailConfig,
  } = usePosCart();

  // Manager/admin may truly purge a draft from the server; cashiers only hide
  // the tab client-side (existing behavior).
  const role = useAuthStore((s) => s.user?.role);
  const canDeleteDraft = role === "admin" || role === "manager";

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentView, setPaymentView] = useState<PaymentView>("summary");
  const [coupon, setCoupon] = useState<CouponResult | null>(null);

  // Controls whether the WorkstationSelector panel is visible.
  const [selectorOpen, setSelectorOpen] = useState(!retailConfig.isConfigured);

  // Keep selectorOpen in sync if config changes (e.g. admin sets it from another panel).
  useEffect(() => {
    if (retailConfig.isConfigured) setSelectorOpen(false);
  }, [retailConfig.isConfigured]);

  // Build the sell-context for useScanResolve. The hook is always instantiated
  // (hooks must not be conditional); when no draft exists orderId="" and the
  // resolver is not called (serial scan button is gated on order existing or we
  // call ensureDraft first in the handleSerialAdd callback below).
  const sellCtx: ScanContext = useMemo(
    () =>
      order?.id
        ? { context: "sell", orderId: order.id }
        : { context: "sell", orderId: "" },
    [order?.id],
  );
  const { resolve: resolveSerial } = useScanResolve(sellCtx);

  // The draft id resolved by the most recent serial-first barcode scan. The scan
  // creates the draft lazily (resolveWithDraft) milliseconds before handleSerialAdd
  // runs, so `order` state is not yet committed; this ref carries the authoritative
  // id into the add/attach chain without relying on a re-render.
  const scanDraftIdRef = useRef<string>("");

  // resolveWithDraft: ensures a draft exists before calling the scan endpoint.
  // Handles the chicken-and-egg: the sell-context scan needs an orderId so the
  // server can derive the warehouse, but a retail draft is created lazily on
  // first item add. For serial-first barcode scan we create the draft upfront
  // here, then call /v1/scan with the now-known orderId.
  const resolveWithDraft = useCallback(
    async (q: string): Promise<ScanResult> => {
      let orderId: string;
      try {
        orderId = await ensureDraft();
      } catch (err) {
        if (err instanceof WorkstationNotConfiguredError) {
          toast.error("Chưa cấu hình kho bán — liên hệ admin");
          setSelectorOpen(true);
          throw err;
        }
        throw err;
      }
      scanDraftIdRef.current = orderId;
      return apiGet<ScanResult>("/v1/scan", { context: "sell", orderId, q });
    },
    [ensureDraft],
  );

  // Global keyboard shortcuts.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "F2") { e.preventDefault(); setSearchOpen(true); }
      if (e.key === "F8") { e.preventDefault(); if (order) setPaymentView("qr"); }
      if (e.key === "F9") { e.preventDefault(); if (order) setPaymentView("cash"); }
      if (e.key === "F11") { e.preventDefault(); handleNewOrder(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const handleAddVariant = useCallback((variantId: string, imageUrl?: string) => {
    cacheVariantImage(variantId, imageUrl);
    addItem.mutate({ variantId }, {
      onError: (err) => {
        if (err instanceof WorkstationNotConfiguredError) {
          toast.error("Chưa cấu hình kho bán — liên hệ admin");
          setSelectorOpen(true);
          return;
        }
        toast.error("Không thể thêm sản phẩm");
      },
    });
  }, [addItem, cacheVariantImage]);

  // Serial-first barcode callback: called by BarcodeInput when kind=serial&ok.
  // Find/create the line then attach the serial. Every step targets the draft id
  // resolved by the scan (scanDraftIdRef) rather than `order` state, which lags
  // the just-created draft within the same tick and would otherwise make the
  // attach throw "no active order" — leaving the product added but no serial.
  const handleSerialAdd = useCallback(
    (variantId: string, serialId: string) => {
      const orderId = scanDraftIdRef.current || order?.id || undefined;
      const existingItem = (order?.items ?? []).find((it) => it.variantId === variantId);

      if (existingItem) {
        const existingIds = (existingItem.serials ?? []).map((s) => s.id);
        if (existingIds.includes(serialId)) {
          toast.info("Serial đã có trong đơn hàng");
          return;
        }
        const mergedIds = [...existingIds, serialId];
        updateItem.mutate(
          { orderId, itemId: existingItem.id, serialIds: mergedIds, quantity: mergedIds.length },
          { onError: (err) => toast.error(translateApiError(err)) },
        );
      } else {
        // No line in current state — add the variant, then attach the serial.
        // Derive the merged serial set + quantity from the server response so a
        // concurrent/rapid same-variant scan can't drop a previously-attached serial.
        addItem.mutate(
          { variantId, orderId },
          {
            onSuccess: (updatedOrder) => {
              const newItem = (updatedOrder.items ?? []).find(
                (it) => it.variantId === variantId,
              );
              if (!newItem) {
                toast.error("Không thể gắn serial: dòng vừa tạo không tìm thấy");
                return;
              }
              const existingIds = (newItem.serials ?? []).map((s) => s.id);
              if (existingIds.includes(serialId)) {
                toast.info("Serial đã có trong đơn hàng");
                return;
              }
              const mergedIds = [...existingIds, serialId];
              updateItem.mutate(
                {
                  orderId: updatedOrder.id,
                  itemId: newItem.id,
                  serialIds: mergedIds,
                  quantity: newItem.quantity,
                },
                { onError: (err) => toast.error(translateApiError(err)) },
              );
            },
            onError: (err) => toast.error(translateApiError(err)),
          },
        );
      }
    },
    [order, addItem, updateItem],
  );

  const handleMultipleResults = useCallback((results: Array<{ id: string }>) => {
    if (results.length > 0) {
      setSearchQuery("");
      setSearchOpen(true);
    }
  }, []);

  const handleUpdateItem = useCallback((itemId: string, updates: { qty?: number; discount?: string; note?: string }) => {
    updateItem.mutate({
      itemId,
      quantity: updates.qty,
      discount: updates.discount,
      note: updates.note,
    });
  }, [updateItem]);

  const handleRemoveItem = useCallback((itemId: string) => {
    removeItem.mutate(itemId);
  }, [removeItem]);

  // Serial attach/detach handlers threaded to CartItemList → CartItemCard → SerialArea.
  const handleAttachSerial = useCallback(
    (itemId: string, mergedSerialIds: string[]) => {
      updateItem.mutate(
        { itemId, serialIds: mergedSerialIds },
        { onError: (err) => toast.error(translateApiError(err)) },
      );
    },
    [updateItem],
  );

  const handleDetachSerial = useCallback(
    (itemId: string, remainingSerialIds: string[]) => {
      updateItem.mutate(
        { itemId, serialIds: remainingSerialIds },
        { onError: (err) => toast.error(translateApiError(err)) },
      );
    },
    [updateItem],
  );

  const handleDiscount = useCallback((amount: string) => {
    patchHeader.mutate({ discountAmount: amount });
  }, [patchHeader]);

  const handleSelectCustomer = useCallback((c: { id: string } | null) => {
    patchHeader.mutate({ customerId: c?.id ?? undefined });
  }, [patchHeader]);

  // Commit with onError to surface serial-validation failures (e.g. race where
  // another till sold the same unit between our scan and commit).
  const handlePayConfirm = useCallback((tenderedAmount: number, couponCode?: string, reason?: string) => {
    commit.mutate(
      { paymentMethod: "cash", tenderedAmount, couponCode, reason },
      {
        onSuccess: (o) => {
          setPaymentView("summary");
          setCoupon(null);
          if (o.id) {
            const wsId = retailConfig.workstationId;
            const url = `/v1/orders/${o.id}/print${wsId ? `?workstation_id=${wsId}` : ""}`;
            apiPost(url).catch(() => {/* non-blocking */});
          }
        },
        onError: (err: unknown) => {
          // Vietnamese serial-validation message comes through translateApiError
          // because the BE returns it as a details string (e.g. "cần đúng 1 serial, đã gắn 0").
          toast.error(translateApiError(err));
        },
      }
    );
  }, [commit, retailConfig.workstationId]);

  const handleQRSuccess = useCallback(() => {
    setPaymentView("summary");
    setCoupon(null);
    if (order?.id) {
      const wsId = retailConfig.workstationId;
      const url = `/v1/orders/${order.id}/print${wsId ? `?workstation_id=${wsId}` : ""}`;
      apiPost(url).catch(() => {/* non-blocking */});
      resumeDraft(order.id).catch(() => {/* non-blocking */});
    }
  }, [order, resumeDraft, retailConfig.workstationId]);

  // Hide the tab client-side and switch to another draft (or clear). Shared by
  // both the cashier path and the manager path's success handler.
  const finishCloseTab = useCallback((orderId: string) => {
    closeDraft(orderId);
    if (order?.id === orderId) {
      setPaymentView("summary");
      setCoupon(null);
      const other = drafts.find((d) => d.id !== orderId);
      if (other) {
        resumeDraft(other.id);
      } else {
        clearCart();
      }
    }
  }, [order, drafts, closeDraft, resumeDraft, clearCart]);

  const handleCloseDraft = useCallback((orderId: string) => {
    if (canDeleteDraft) {
      // Manager/admin: purge server-side, and only hide the tab once the delete
      // succeeds — a failed delete (e.g. order is no longer a draft) must not
      // hide a still-live order from this browser forever.
      deleteDraft.mutate(orderId, {
        onSuccess: () => finishCloseTab(orderId),
        onError: (err) => toast.error(translateApiError(err)),
      });
    } else {
      // Cashier: client-side hide only (unchanged behavior).
      finishCloseTab(orderId);
    }
  }, [canDeleteDraft, deleteDraft, finishCloseTab]);

  const handleNewOrder = useCallback(() => {
    setPaymentView("summary");
    setCoupon(null);
    if (!retailConfig.isConfigured) {
      toast.error("Chưa cấu hình kho bán — liên hệ admin");
      setSelectorOpen(true);
      return;
    }
    createDraft().catch((err: unknown) => {
      if (err instanceof WorkstationNotConfiguredError) {
        toast.error("Chưa cấu hình kho bán — liên hệ admin");
        setSelectorOpen(true);
        return;
      }
      toast.error(translateApiError(err));
    });
  }, [createDraft, retailConfig.isConfigured]);

  const items = order?.items ?? [];
  const subtotal = parseFloat(order?.subtotal ?? "0");
  const isPaid = order?.status === "paid";

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden">
      {/* Warehouse badge / config row */}
      <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-1 text-xs shrink-0">
        {retailConfig.isConfigured ? (
          <button
            type="button"
            onClick={() => setSelectorOpen((v) => !v)}
            className="flex items-center gap-1.5 rounded-md bg-green-50 border border-green-200 px-2 py-1 text-green-700 hover:bg-green-100 transition-colors dark:bg-green-950/30 dark:border-green-800 dark:text-green-300"
            title="Nhấn để đổi cấu hình kho bán"
          >
            <WarehouseIcon size={12} className="shrink-0" />
            <span className="font-medium">Đang bán từ: {retailConfig.warehouseName ?? retailConfig.workstationName}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setSelectorOpen(true)}
            className="flex items-center gap-1.5 rounded-md bg-amber-50 border border-amber-300 px-2 py-1 text-amber-700 hover:bg-amber-100 transition-colors dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-400"
          >
            <WarehouseIcon size={12} className="shrink-0" />
            <span className="font-medium">Chưa cấu hình kho bán — nhấn để cấu hình</span>
          </button>
        )}

        {/* Inline selector panel */}
        {selectorOpen && (
          <div className="absolute z-50 top-[4.5rem] left-3">
            <WorkstationSelector
              channel="retail"
              onConfigured={() => setSelectorOpen(false)}
            />
          </div>
        )}
      </div>

      {/* Unified toolbar: tabs + barcode + customer/coupon chips */}
      <PosToolbar
        drafts={drafts}
        activeOrderId={order?.id}
        onSelectDraft={resumeDraft}
        onCloseDraft={handleCloseDraft}
        onNewOrder={handleNewOrder}
        onAddVariant={handleAddVariant}
        onMultipleResults={handleMultipleResults}
        barcodeDisabled={isPaid || addItem.isPending || !retailConfig.isConfigured}
        selectedCustomerId={order?.customerId}
        onSelectCustomer={handleSelectCustomer}
        orderType={orderType}
        onSetOrderType={setOrderType}
        order={order}
        isPaid={isPaid}
        subtotal={subtotal}
        coupon={coupon}
        onApplyCoupon={setCoupon}
        loading={addItem.isPending}
        serialResolver={resolveWithDraft}
        onSerialAdd={handleSerialAdd}
        onSerialError={(reason) => toast.error(reason)}
      />

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: cart — pb-16 on mobile reserves space above the sticky bar */}
        <div className="flex flex-1 flex-col overflow-auto pb-16 lg:pb-0">
          {isPaid ? (
            <div className="flex flex-1 items-center justify-center flex-col gap-4 text-center">
              <div className="text-2xl font-bold text-green-600">Thanh toán thành công!</div>
              <div className="text-muted-foreground">Đơn {order?.code}</div>
              <button
                onClick={handleNewOrder}
                className="rounded-md bg-primary px-6 py-2 text-primary-foreground font-medium"
              >
                Đơn hàng mới (F11)
              </button>
            </div>
          ) : (
            <CartItemList
              items={items}
              imageMap={imageCache}
              onUpdateItem={handleUpdateItem}
              onRemoveItem={handleRemoveItem}
              loading={addItem.isPending || updateItem.isPending || removeItem.isPending}
              serialResolver={resolveSerial}
              onAttachSerial={handleAttachSerial}
              onDetachSerial={handleDetachSerial}
            />
          )}
        </div>

        {/* Right: payment panel — hidden on mobile */}
        <div className="hidden lg:flex w-[360px] shrink-0 border-l bg-card flex-col">
          {order && (
            <div className="border-b px-4 py-2 text-xs text-muted-foreground font-mono">
              {order.code}
            </div>
          )}
          <PaymentPanel
            order={order}
            coupon={coupon}
            onDiscount={handleDiscount}
            onPayCash={handlePayConfirm}
            onPayQR={() => {}}
            onQRSuccess={handleQRSuccess}
            onNewOrder={handleNewOrder}
            loading={commit.isPending}
            view={paymentView}
            onViewChange={setPaymentView}
            selectedCustomerId={order?.customerId}
            onSelectCustomer={handleSelectCustomer}
          />
        </div>
      </div>

      {/* Mobile sticky bottom payment bar — hidden on lg+ */}
      <MobilePaymentBar
        itemCount={items.length}
        order={order}
        coupon={coupon}
        onDiscount={handleDiscount}
        onPayCash={handlePayConfirm}
        onPayQR={() => {}}
        onQRSuccess={handleQRSuccess}
        onNewOrder={handleNewOrder}
        loading={commit.isPending}
        view={paymentView}
        onViewChange={setPaymentView}
      />

      {/* Modals — all preserved */}
      <ProductSearchModal
        open={searchOpen}
        initialQuery={searchQuery}
        onSelect={handleAddVariant}
        onClose={() => setSearchOpen(false)}
      />
    </div>
  );
}
